import { useCallback, useMemo } from 'react';

import { capitalize } from '@myn/mui';

import {
  adjustSaturation,
  CHART_COLORS_IN_ORDER_TAILWIND,
  colorizeData,
  colorToHex,
} from '../../../helpers/charts.js';
import type {
  DataEntry,
  GroupedEntry,
  PieChartData,
  PrimitiveDataframe,
  PrimitiveDataframeRow,
} from '../../../types/charts.js';

type PieChartOptions = {
  x: string;
  y: string[];
  dataframe: PrimitiveDataframe;
};

const PIE_LEVEL_TWO = 2;
const UNIQUE_CHANNEL_TYPE = 2;

const isNumericValue = (
  item: Record<string, PrimitiveDataframeRow>,
  key: string,
): item is Record<string, PrimitiveDataframeRow> &
  Record<typeof key, number> => {
  return typeof item[key] === 'number';
};

export const usePieChartData = ({ x, y, dataframe }: PieChartOptions) => {
  const colors = CHART_COLORS_IN_ORDER_TAILWIND;

  const isOnlineAndOfflineExist = useMemo(() => {
    const onlineAndOffline = dataframe
      .map((d) => d.channelType)
      .filter((d) => d === 'Online' || d === 'Offline');

    const uniqueChannelType = new Set(onlineAndOffline);

    return uniqueChannelType.size === UNIQUE_CHANNEL_TYPE;
  }, [dataframe]);

  const isTwoLevel = y.length === PIE_LEVEL_TWO && isOnlineAndOfflineExist;
  const isTwoButWithMissingData =
    y.length === PIE_LEVEL_TWO && !isOnlineAndOfflineExist;
  const isOneLevel = y.length === 1;

  const prepareTwoLevelPieData = useCallback(
    (data: DataEntry[], nameKey: string, dataKey: string): GroupedEntry[] => {
      const grouped = new Map();

      for (const item of data) {
        const key = item[nameKey];
        const value = item[dataKey];

        if (grouped.has(key)) {
          grouped.set(key, grouped.get(key) + value);
        } else {
          grouped.set(key, value);
        }
      }

      return Array.from(grouped.entries()).map(([key, value], i) => ({
        [nameKey]: key,
        [dataKey]: value,
        fill: colorToHex(colors[i % colors.length]),
      }));
    },
    [colors],
  );

  const getNameKey = (): string => {
    if (isTwoButWithMissingData || isTwoLevel) {
      return typeof dataframe[0][y[0]] === 'string' ? y[0] : y[1];
    }

    return y[0];
  };
  const getDataKey = (): string => {
    if (isOneLevel) {
      return y[0];
    }

    if (nameKey === y[0]) {
      return y[1];
    }

    return y[0];
  };

  const nameKey = getNameKey();
  const dataKey = getDataKey();

  const chartData: PieChartData[] = useMemo(() => {
    const baseData = isTwoLevel
      ? prepareTwoLevelPieData(dataframe, nameKey, dataKey)
      : colorizeData(dataframe, colors);

    const getChartValue = (item: GroupedEntry) => {
      if (isTwoLevel) {
        return item[nameKey] as string | number;
      }

      if (typeof item[x] === 'string') {
        return capitalize(item[x]);
      }

      return Number(item[x]);
    };

    const createChildren = (parentItem: GroupedEntry) => {
      return dataframe
        .filter((child) => child[nameKey] === parentItem[nameKey])
        .map((child, index) => ({
          ...child,
          fill: adjustSaturation(parentItem.fill, 0.5 - index * 0.1),
          value:
            typeof child[x] === 'string'
              ? capitalize(child[x])
              : Number(child[x]),
          dataKeyValue: child[dataKey],
        }));
    };

    return baseData
      .map((item) => ({
        ...item,
        dataKeyValue: item[dataKey] as number,
        value: getChartValue(item),
        children: isTwoLevel ? createChildren(item) : [],
      }))
      .sort((a, b) => b.dataKeyValue - a.dataKeyValue);
  }, [
    dataframe,
    isTwoLevel,
    nameKey,
    dataKey,
    x,
    prepareTwoLevelPieData,
    colors,
  ]);

  const percentageByValue = useMemo(() => {
    const numericItems = dataframe.filter((item) =>
      isNumericValue(item, nameKey),
    );
    const total = numericItems.reduce((sum, item) => sum + item[nameKey], 0);
    return numericItems.map((item) => (item[nameKey] / total) * 100);
  }, [dataframe, nameKey]);

  return {
    chart: {
      chartData,
      nameKey,
      dataKey,
    },
    percentageByValue,
    isTwoLevel,
  };
};
